'use client';

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import starImage from '../../assets/images/star.svg?inline';
import arrowRightImage from '../../assets/images/arrow-right.svg?inline';
import image1 from '../../assets/images/testimonials/EpisodePic.png';
import image2 from '../../assets/images/testimonials/EpisodePic2.png';
import webpImage1 from '../../assets/images/testimonials/EpisodePic.png?webp';
import webpImage2 from '../../assets/images/testimonials/EpisodePic2.png?webp';
import styles from './Testimonials.module.css';

const cx = (...classes: (string | undefined)[]) =>
  classes.filter(Boolean).join(' ');

const TIMER_IN_MILLISECONDS = 10000;

const testimonials = [
  {
    id: 1,
    stars: 5,
    image: image1,
    webp: webpImage1,
    headline: 'Simply amazing!',
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut rhoncus dolor eu ipsum condimentum pellentesque. Suspendisse pretium, orci viverra venenatis congue, odio lorem tempus ante.',
    author: 'Laruga Glaser - Yoga coach',
  },
  {
    id: 2,
    stars: 4,
    image: image2,
    webp: webpImage2,
    headline: 'Nothing is better!',
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut rhoncus dolor eu ipsum condimentum pellentesque. Suspendisse pretium, orci viverra venenatis congue, odio lorem tempus ante.',
    author: 'Laruga Glaser - Yoga coach',
  },
  {
    id: 3,
    stars: 5,
    image: image1,
    webp: webpImage1,
    headline: 'I wish I had this when I was alive!',
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut rhoncus dolor eu ipsum condimentum pellentesque. Suspendisse pretium, orci viverra venenatis congue, odio lorem tempus ante.',
    author: 'Laruga Glaser - Yoga coach',
  },
  {
    id: 4,
    stars: 2,
    image: image2,
    webp: webpImage2,
    headline: 'I invested in it, you should buy this app!',
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Ut rhoncus dolor eu ipsum condimentum pellentesque. Suspendisse pretium, orci viverra venenatis congue, odio lorem tempus ante.',
    author: 'Laruga Glaser - Yoga coach',
  },
];

export const Testimonials = ({ className }: { className?: string }) => {
  const [selected, setSelected] = useState(0);
  const progressCircleRef = useRef<SVGCircleElement>(null);
  const intervalRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastChangedAt = useRef<Date>(new Date());

  const next = useCallback(() => {
    lastChangedAt.current = new Date();
    setSelected((selected) => (selected + 1) % testimonials.length);
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
    }
    intervalRef.current = setTimeout(() => {
      next();
    }, TIMER_IN_MILLISECONDS) as unknown as number;
  }, []);

  const nextAnimation = useCallback(() => {
    const progress =
      (new Date().getTime() - lastChangedAt.current.getTime()) /
      TIMER_IN_MILLISECONDS;

    if (progressCircleRef.current) {
      progressCircleRef.current.style.strokeDashoffset = `${
        nextButtonCircumference - progress * nextButtonCircumference
      }`;
    }

    rafRef.current = requestAnimationFrame(nextAnimation);
  }, []);

  useEffect(() => {
    intervalRef.current = setTimeout(() => {
      next();
    }, TIMER_IN_MILLISECONDS) as unknown as number;

    rafRef.current = requestAnimationFrame(nextAnimation);

    return () => {
      if (intervalRef.current && rafRef.current) {
        clearTimeout(intervalRef.current);
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  const handleNext = useCallback(() => {
    next();
  }, []);

  return (
    <div className={cx(styles.root, className)}>
      <ImagesWrapper selected={selected} />
      <TestimonialsWrapper selected={selected} />
      <NextButton progressCircleRef={progressCircleRef} onClick={handleNext} />
    </div>
  );
};

const ImagesWrapper = ({
  className,
  selected,
}: {
  className?: string;
  selected: number;
}) => {
  return (
    <div className={cx(styles.imagesWrapper, className)}>
      {testimonials.map((testimonial, index) => {
        const classNameSelected = selected === index ? styles.selected : '';
        const classNameNext =
          index === (selected + 1) % testimonials.length ? styles.next : '';
        const classNameNext2 =
          index === (selected + 2) % testimonials.length ? styles.next2 : '';
        const classNamePrevious =
          index === (selected - 1 + testimonials.length) % testimonials.length
            ? styles.previous
            : '';
        return (
          <Image
            src={testimonial.image}
            key={testimonial.id}
            alt={testimonial.author}
            webp={testimonial.webp}
            className={cx(
              classNameSelected,
              classNameNext,
              classNameNext2,
              classNamePrevious,
            )}
          />
        );
      })}
    </div>
  );
};

const Image = ({
  className,
  alt,
  src,
  webp,
}: {
  className?: string;
  alt: string;
  src: any;
  webp: any;
}) => {
  return (
    <picture>
      <source srcSet={webp} type="image/webp" />
      <source srcSet={src} type="image/png" />
      <img
        loading="lazy"
        src={src}
        alt={alt}
        className={cx(styles.image, className)}
      />
    </picture>
  );
};

const TestimonialsWrapper = ({
  className,
  selected,
}: {
  className?: string;
  selected: number;
}) => {
  return (
    <div className={cx(styles.testimonialsWrapper, className)}>
      {testimonials.map((testimonial, index) => {
        const classNameSelected = selected === index ? styles.selected : '';
        return (
          <Testimonial
            testimonial={testimonial}
            key={testimonial.id}
            className={classNameSelected}
          />
        );
      })}
    </div>
  );
};

const Testimonial = ({
  className,
  testimonial,
}: {
  className?: string;
  testimonial: (typeof testimonials)[0];
}) => {
  return (
    <div
      className={cx(styles.testimonial, className)}
      itemProp="review"
      itemScope
      itemType="https://schema.org/Review"
    >
      <div>
        <Stars stars={testimonial.stars} />
        <h4 className={styles.headline} itemProp="name">
          “{testimonial.headline}”
        </h4>
        <p className={styles.description} itemProp="reviewBody">
          “{testimonial.description}”
        </p>
        <p className={styles.author} itemProp="author">
          {testimonial.author}
        </p>
      </div>
    </div>
  );
};

const Stars = memo(function Stars({
  className,
  stars,
}: {
  className?: string;
  stars: number;
}) {
  const starsImages = Array.from({ length: stars }, (v, i) => (
    <img loading="lazy" src={starImage.src} alt="star" key={i} />
  ));
  return (
    <div className={cx(styles.stars, className)}>
      <meta itemProp="worstRating" content="1" />
      <meta itemProp="ratingValue" content={stars.toString()} />
      <meta itemProp="bestRating" content="5" />
      {starsImages}
    </div>
  );
});

const nextButtonCircumference = 29 * 2 * Math.PI;

const NextButton = ({
  className,
  progressCircleRef,
  onClick,
}: {
  className?: string;
  progressCircleRef: React.Ref<SVGCircleElement>;
  onClick?: () => void;
}) => {
  return (
    <div
      className={cx(styles.nextButton, className)}
      onClick={onClick}
      onKeyUp={onClick}
      role="button"
      tabIndex={0}
    >
      <svg height="60" width="60">
        <circle
          strokeWidth="2"
          strokeDasharray={nextButtonCircumference}
          fill="transparent"
          r="29"
          cx="30"
          cy="30"
          ref={progressCircleRef}
        />
      </svg>
      <img loading="lazy" src={arrowRightImage.src} alt="Next" />
    </div>
  );
};
